// One field, rendered.
//
// Three things about this component are decisions rather than defaults.
//
// It holds no copy of an answer. Values arrive as props and leave as callbacks,
// so there is exactly one place an attendee's answer lives and exactly one
// thing session privacy has to clear. A component that kept, say, the words
// somebody typed into an "other" box so it could restore them if they changed
// their mind would be a second copy of an answer that the clear does not know
// about, which is how a shared screen shows the previous person's data.
//
// Autofill is off everywhere. This is a browser on a tablet used by four
// hundred people in a day, and the browser's helpful suggestion of the last
// name typed into this box is a data-protection incident offered as a
// convenience. The fields also carry no `name`, because that is the other half
// of what autofill matches on.
//
// Grouped choices are real radios and checkboxes inside a fieldset. Arrow-key
// movement within a group, the announced role, the group label and the
// required semantics all come free, and not one of them is a thing anybody
// remembers to reimplement on a div.

import type { ChangeEvent, ReactNode } from "react";
import {
  encodePhoneValue,
  nationalDigits,
  phoneCountryOf,
  phoneLooksTooLong,
  PHONE_COUNTRIES,
  PHONE_TOO_LONG,
} from "./phone.js";
import {
  fieldLabel,
  fieldOptions,
  isOtherValue,
  type FieldDefinition,
  type FieldOption,
  type FieldValue,
} from "./types.js";
import { OTHER_OPTION_ID, countCharacters, effectiveMaxLength, type Verdict } from "./validate.js";

export type FieldProps = {
  field: FieldDefinition;
  value: FieldValue;
  onChange: (field_id: string, value: FieldValue) => void;
  /**
   * Shown when the caller decides to show it, which is on leaving the field or
   * on trying to finish, never on the first keystroke. A message that appears
   * while somebody is still typing their answer is a message telling them they
   * got it wrong before they finished getting it right.
   */
  problem?: Verdict | undefined;
  disabled?: boolean | undefined;
  onBlur?: ((field_id: string) => void) | undefined;
  labelsAsPlaceholders?: boolean | undefined;
};

/** The element the validation summary sends focus to. */
export const fieldControlId = (field_id: string): string => `xp-${field_id}-input`;

export function Field({
  field,
  value,
  onChange,
  problem,
  disabled,
  onBlur,
  labelsAsPlaceholders = false,
}: FieldProps): ReactNode {
  const id = fieldControlId(field.field_id);
  const helpId = `xp-${field.field_id}-help`;
  const msgId = `xp-${field.field_id}-msg`;
  const countId = `xp-${field.field_id}-count`;

  const suggestId = `xp-${field.field_id}-did-you-mean`;

  const blocked = problem?.state === "block";
  const message = problem && problem.state !== "ok" ? problem.message : null;
  const suggestion = problem?.state === "ok" ? problem.suggestion : undefined;
  const max = effectiveMaxLength(field);
  const typed = typeof value === "string" ? value : "";
  // Live, unlike other verdicts: once the box is already longer than this
  // country uses, they are past unfinished and into a number that looks wrong.
  // A refusal still waits for blur. This line does not.
  const phoneCountry =
    field.type === "phone" ? phoneCountryOf(typed, field.default_country ?? "GB") : "";
  const liveTooLong =
    field.type === "phone" && typed !== "" && phoneLooksTooLong(typed, phoneCountry);
  const shownMessage = message ?? (liveTooLong ? PHONE_TOO_LONG : null);
  const shownAsWarn = problem?.state === "warn" || (message === null && liveTooLong);
  const counted =
    field.type === "text" ||
    field.type === "long_text" ||
    (field.type === "survey_question" && field.answer_format === "free_text");
  // Only near the ceiling. A counter that is on the whole time is a target,
  // and somebody writing two words does not need to know they have 198 left.
  const showCount = counted && countCharacters(typed) > max - 25;

  const describedBy = [
    field.help_text ? helpId : null,
    shownMessage ? msgId : null,
    suggestion === undefined ? null : suggestId,
    showCount ? countId : null,
  ]
    .filter((x): x is string => x !== null)
    .join(" ");

  const help = field.help_text ? <p className="xp-field__help" id={helpId}>{field.help_text}</p> : null;

  // The region exists whether or not it holds anything, because a live region
  // added to the page at the same moment as its text is a live region that
  // announces nothing.
  //
  // The suggestion shares the region with the message rather than having one of
  // its own, and it can, because the two cannot both be here: a refused address
  // is told what to do about it and a well-formed one is asked a question, and
  // suggest.ts is only ever consulted about an address that passed.
  //
  // WHY THE FIELD NAMES ITSELF IN HERE.
  //
  // FieldSet fills this region on the way out of a field rather than on the way
  // in, so by the time anything is announced focus has already moved to the next
  // one. "Fill this in." arriving politely a moment after "Email, text field" is
  // heard as being about the email. The name goes first so the announcement is
  // "Company" then "Fill this in.", which is what a sighted person gets from the
  // message sitting under the box it belongs to.
  //
  // Hidden rather than drawn, because on screen it would be the label repeated
  // an inch below itself. In its own element rather than prefixed onto the
  // message, so speech pauses between the two without this having to guess
  // whether a label already ends in a question mark. And outside both elements
  // aria-describedby points at, so somebody who comes back to the field hears
  // the message once rather than the name and then the message.
  const live = (
    <div aria-live="polite">
      {shownMessage !== null || suggestion !== undefined ? (
        <span className="xp-vh">{fieldLabel(field)}</span>
      ) : null}
      {shownMessage ? (
        <p className={`xp-field__msg${shownAsWarn ? " xp-field__msg--warn" : ""}`} id={msgId}>
          {shownMessage}
        </p>
      ) : null}
      {suggestion === undefined ? null : (
        <div className="xp-suggest">
          {/* Asked, not asserted. This is a guess against a short list of
              domains, and the person is the only one who knows whether their
              own address is the odd one that sits a keystroke from a big one. */}
          <p className="xp-suggest__q" id={suggestId}>Did you mean {suggestion}?</p>
          {/* The whole address on the button rather than "Yes". The accessible
              name has to carry what the control does on its own, because
              somebody moving between controls hears it out of order with the
              question above it, and because a button reading "Yes" beside a
              field is a button people press to get on with it. */}
          <button
            type="button"
            className="xp-suggest__use"
            disabled={disabled === true}
            onClick={() => {
              onChange(field.field_id, suggestion);
              // This button is about to stop existing, and a browser drops
              // focus to the body when the focused element goes, which strands
              // anybody not using a finger. The field is where the change
              // landed and where somebody would look next, so focus goes there.
              // On a tablet that also brings the keyboard back up, which is the
              // lesser of the two.
              document.getElementById(id)?.focus();
            }}
          >
            Use {suggestion}
          </button>
        </div>
      )}
    </div>
  );

  const count = showCount ? (
    <p className="xp-field__count" id={countId}>
      {max - countCharacters(typed)} characters left
    </p>
  ) : null;

  const label = (
    <>
      {fieldLabel(field)}
      {field.required ? <span className="xp-field__need"> (needed)</span> : null}
    </>
  );
  const usePlaceholderLabel =
    labelsAsPlaceholders &&
    (field.type === "text" || field.type === "email" || field.type === "number");
  const placeholderLabel = `${fieldLabel(field)}${field.required ? "*" : ""}`;

  // Common attributes for anything that takes typing.
  //
  // `required` is left off deliberately in favour of aria-required: the native
  // bubble is unstyleable, speaks its own language, and says "Please fill in
  // this field" in a voice that is not Nova's.
  //
  // `maxLength` is left off for a better reason. It truncates a paste in
  // silence, so somebody pasting a long company name loses the end of it and
  // is told nothing. The length is enforced by validateField instead, which
  // can say how much is over, and the counter appears before they get there.
  const typing = {
    id,
    className: `xp-input${blocked ? " xp-input--bad" : ""}`,
    disabled: disabled === true,
    "aria-describedby": describedBy || undefined,
    "aria-invalid": blocked || undefined,
    "aria-required": field.required || undefined,
    autoComplete: "off" as const,
    onBlur: () => onBlur?.(field.field_id),
  };

  const text = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange(field.field_id, event.target.value);

  const labelled = (control: ReactNode) => (
    <div className={`xp-field${usePlaceholderLabel ? " xp-field--placeholder" : ""}`}>
      <label className={`xp-field__label${usePlaceholderLabel ? " xp-vh" : ""}`} htmlFor={id}>
        {label}
      </label>
      {help}
      {control}
      {count}
      {live}
    </div>
  );

  const grouped = (control: ReactNode, role: "radiogroup" | "group") => (
    <fieldset className="xp-field">
      <legend className="xp-field__label">{label}</legend>
      {help}
      <div
        className={field.type === "survey_question" && field.answer_format === "rating_scale" ? "xp-scale" : "xp-opts"}
        role={role}
        aria-describedby={describedBy || undefined}
        aria-invalid={blocked || undefined}
        aria-required={field.required || undefined}
      >
        {control}
      </div>
      {live}
    </fieldset>
  );

  switch (field.type) {
    case "text":
      return labelled(
        <input
          {...typing}
          type="text"
          value={typed}
          placeholder={field.placeholder ?? (usePlaceholderLabel ? placeholderLabel : undefined)}
          onChange={text}
        />,
      );

    case "long_text":
      return labelled(
        <textarea
          {...typing}
          className={`xp-textarea${blocked ? " xp-textarea--bad" : ""}`}
          rows={4}
          value={typed}
          onChange={text}
        />,
      );

    case "number":
      // Not type="number". It refuses to hand back what was typed when the
      // entry is half finished, it grows spinners nobody can hit at 56px, and
      // it changes value on a scroll. inputMode gets the numeric keypad up,
      // which is the only part of type="number" that was wanted.
      return labelled(
        <input
          {...typing}
          type="text"
          inputMode={field.integer_only ? "numeric" : "decimal"}
          value={typeof value === "number" ? String(value) : typed}
          placeholder={usePlaceholderLabel ? placeholderLabel : undefined}
          onChange={text}
        />,
      );

    case "date":
      return labelled(
        <input {...typing} type="date" value={typed} min={field.earliest} max={field.latest} onChange={text} />,
      );

    case "email":
      return labelled(
        <input
          {...typing}
          type="email"
          inputMode="email"
          spellCheck={false}
          value={typed}
          placeholder={usePlaceholderLabel ? placeholderLabel : undefined}
          onChange={text}
        />,
      );

    case "phone": {
      const fallbackCountry = field.default_country ?? "GB";
      const country = phoneCountryOf(typed, fallbackCountry);
      const digits = nationalDigits(typed);
      return labelled(
        <div className="xp-phone">
          <select
            className={`xp-input xp-phone__country${blocked ? " xp-input--bad" : ""}`}
            aria-label="Country"
            disabled={disabled === true}
            value={PHONE_COUNTRIES.some((entry) => entry.code === country) ? country : fallbackCountry}
            onChange={(event) => onChange(field.field_id, encodePhoneValue(event.target.value, digits))}
            onBlur={() => onBlur?.(field.field_id)}
          >
            {PHONE_COUNTRIES.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.label}
              </option>
            ))}
          </select>
          <input
            {...typing}
            className={`xp-input xp-phone__number${blocked ? " xp-input--bad" : ""}`}
            type="text"
            inputMode="numeric"
            value={digits}
            onChange={(event) =>
              onChange(field.field_id, encodePhoneValue(country, event.target.value.replace(/\D/g, "")))
            }
          />
        </div>
      );
    }

    case "boolean":
      // Two radios rather than one checkbox, and the reason is that a checkbox
      // has two states where this field has three: yes, no, and not answered.
      // A required checkbox cannot tell "no" from "has not got there yet".
      return grouped(
        <>
          {[
            { id: "true", label: field.true_label, on: value === true },
            { id: "false", label: field.false_label, on: value === false },
          ].map((choice, index) => (
            <label className="xp-opt" key={choice.id}>
              <input
                className="xp-opt__in"
                id={index === 0 ? id : undefined}
                type="radio"
                name={`xp-${field.field_id}`}
                checked={choice.on}
                disabled={disabled === true}
                onChange={() => onChange(field.field_id, choice.id === "true")}
                onBlur={() => onBlur?.(field.field_id)}
              />
              <span className="xp-opt__t">{choice.label}</span>
            </label>
          ))}
        </>,
        "radiogroup",
      );

    case "single_select":
    case "status": {
      const options: FieldOption[] = [...fieldOptions(field)];
      if (field.type === "single_select" && field.allow_other) {
        options.push({ id: OTHER_OPTION_ID, label: field.other_label ?? "Something else" });
      }
      const chosen = isOtherValue(value) ? value.option_id : typeof value === "string" ? value : "";
      const otherText = isOtherValue(value) ? value.other_text : "";

      return grouped(
        <>
          {options.map((option, index) => (
            <label className="xp-opt" key={option.id}>
              <input
                className="xp-opt__in"
                id={index === 0 ? id : undefined}
                type="radio"
                name={`xp-${field.field_id}`}
                checked={chosen === option.id}
                disabled={disabled === true}
                onChange={() =>
                  onChange(
                    field.field_id,
                    option.id === OTHER_OPTION_ID && field.type === "single_select" && field.allow_other
                      ? { option_id: OTHER_OPTION_ID, other_text: "" }
                      : option.id,
                  )
                }
                onBlur={() => onBlur?.(field.field_id)}
              />
              <span className="xp-opt__t">{option.label}</span>
            </label>
          ))}
          {chosen === OTHER_OPTION_ID ? (
            <input
              className="xp-input"
              type="text"
              autoComplete="off"
              aria-label={field.type === "single_select" ? (field.other_label ?? "Something else") : "Something else"}
              value={otherText}
              disabled={disabled === true}
              onChange={(event) =>
                onChange(field.field_id, { option_id: OTHER_OPTION_ID, other_text: event.target.value })
              }
              onBlur={() => onBlur?.(field.field_id)}
            />
          ) : null}
        </>,
        "radiogroup",
      );
    }

    case "multi_select": {
      const chosen = Array.isArray(value) ? value : [];
      return grouped(
        <>
          {field.options.map((option, index) => (
            <label className="xp-opt" key={option.id}>
              <input
                className="xp-opt__in"
                id={index === 0 ? id : undefined}
                type="checkbox"
                checked={chosen.includes(option.id)}
                disabled={disabled === true}
                onChange={() =>
                  onChange(
                    field.field_id,
                    chosen.includes(option.id)
                      ? chosen.filter((x) => x !== option.id)
                      : [...chosen, option.id],
                  )
                }
                onBlur={() => onBlur?.(field.field_id)}
              />
              <span className="xp-opt__t">{option.label}</span>
            </label>
          ))}
        </>,
        "group",
      );
    }

    case "survey_question":
      switch (field.answer_format) {
        case "free_text":
          return labelled(
            <textarea
              {...typing}
              className={`xp-textarea${blocked ? " xp-textarea--bad" : ""}`}
              rows={4}
              value={typed}
              onChange={text}
            />,
          );

        case "single_choice": {
          const chosen = typeof value === "string" ? value : "";
          return grouped(
            <>
              {(field.options ?? []).map((option, index) => (
                <label className="xp-opt" key={option.id}>
                  <input
                    className="xp-opt__in"
                    id={index === 0 ? id : undefined}
                    type="radio"
                    name={`xp-${field.field_id}`}
                    checked={chosen === option.id}
                    disabled={disabled === true}
                    onChange={() => onChange(field.field_id, option.id)}
                    onBlur={() => onBlur?.(field.field_id)}
                  />
                  <span className="xp-opt__t">{option.label}</span>
                </label>
              ))}
            </>,
            "radiogroup",
          );
        }

        case "multi_choice": {
          const chosen = Array.isArray(value) ? value : [];
          return grouped(
            <>
              {(field.options ?? []).map((option, index) => (
                <label className="xp-opt" key={option.id}>
                  <input
                    className="xp-opt__in"
                    id={index === 0 ? id : undefined}
                    type="checkbox"
                    checked={chosen.includes(option.id)}
                    disabled={disabled === true}
                    onChange={() =>
                      onChange(
                        field.field_id,
                        chosen.includes(option.id)
                          ? chosen.filter((x) => x !== option.id)
                          : [...chosen, option.id],
                      )
                    }
                    onBlur={() => onBlur?.(field.field_id)}
                  />
                  <span className="xp-opt__t">{option.label}</span>
                </label>
              ))}
            </>,
            "group",
          );
        }

        case "rating_scale": {
          const min = field.scale_min ?? 1;
          const maxPoint = field.scale_max ?? 5;
          const points = Array.from({ length: maxPoint - min + 1 }, (_, step) => min + step);
          const chosen = typeof value === "number" ? value : Number(value);

          // The ends of a scale are labelled under it, where a sighted person
          // reads them once. Read out, that would be five numbers followed by
          // two words with nothing tying them together, so the words go on the
          // ends they belong to instead.
          const spoken = (point: number): string => {
            if (point === min && field.scale_min_label) return `${point}, ${field.scale_min_label}`;
            if (point === maxPoint && field.scale_max_label) return `${point}, ${field.scale_max_label}`;
            return String(point);
          };

          return (
            <fieldset className="xp-field">
              <legend className="xp-field__label">{label}</legend>
              {help}
              <div
                className="xp-scale"
                role="radiogroup"
                aria-describedby={describedBy || undefined}
                aria-invalid={blocked || undefined}
                aria-required={field.required || undefined}
              >
                {points.map((point, index) => (
                  <label className="xp-scale__opt" key={point}>
                    <input
                      className="xp-opt__in xp-vh"
                      id={index === 0 ? id : undefined}
                      type="radio"
                      name={`xp-${field.field_id}`}
                      checked={chosen === point}
                      disabled={disabled === true}
                      onChange={() => onChange(field.field_id, point)}
                      onBlur={() => onBlur?.(field.field_id)}
                    />
                    <span aria-hidden="true">{point}</span>
                    <span className="xp-vh">{spoken(point)}</span>
                  </label>
                ))}
              </div>
              {field.scale_min_label || field.scale_max_label ? (
                <p className="xp-scale__ends" aria-hidden="true">
                  <span>{field.scale_min_label}</span>
                  <span>{field.scale_max_label}</span>
                </p>
              ) : null}
              {live}
            </fieldset>
          );
        }
      }
  }
}
